/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import PdfViewer from './components/PdfViewer';

import ExcelViewer from './components/ExcelViewer';
import DocViewer from './components/DocViewer';

import { FILE_TYPE } from './constants';

import JsonViewer from './components/JsonViewer';

import { getByodUploadFiles } from 'store/features/report/report.selector';

import { fetchByodUploadFilesApi } from 'store/features/report/report.action';
import Loading from 'components/loading';

const ViewFilePage: React.FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const name = params.get('name') || '';
  const pcId = params.get('pcId') || '';
  const connectionUuid = params.get('connectionUuid') || '';

  const dispatch = useDispatch();
  const { data: byodData } = useSelector(getByodUploadFiles);
  const [fileObj, setFileObj] = useState(null);
  const [fileType, setFileType] = useState<string | null>(null);

  const isLoading = useSelector(getByodUploadFiles).loading;

  useEffect(() => {
    if (connectionUuid && pcId) {
      dispatch(fetchByodUploadFilesApi({ params: { connectionUuid, pushCommunicationId: pcId } }));
    }
  }, [connectionUuid, pcId]);

  useEffect(() => {
    if (byodData?.length > 0 && pcId && name) {
      const fileList = byodData?.find((dataObj) => dataObj.pushCommunicationId === pcId);
      const fileObj = fileList?.files?.find((file) => file.name === name);
      if (fileObj) {
        // extract extension as file type
        const parts = fileObj.name?.split('.') || [];
        const ext = (parts.length > 1 ? parts.pop() : '')?.toLowerCase() || '';
        let type: string | null = ext;
        switch (ext) {
          case FILE_TYPE.XLS:
          case FILE_TYPE.XLSX:
            type = FILE_TYPE.XLSX;
            break;
          case FILE_TYPE.CSV:
          case FILE_TYPE.DOCX:
          case FILE_TYPE.PDF:
          case FILE_TYPE.JSON:
            type = ext;
            break;
          default:
            type = null;
        }
        setFileType(type);
      }
      setFileObj(fileObj);
    }
  }, [byodData, pcId, name]);

  return (
    <>
      {isLoading ? (
        <Loading loading={isLoading} testId={'view-file-page-loader'} />
      ) : (
        <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>
          {fileObj ? (
            <>
              <div>
                {fileType === FILE_TYPE.PDF && <PdfViewer src={fileObj.path} name={fileObj.name} />}
              </div>
              <div>
                {[FILE_TYPE.XLSX, FILE_TYPE.CSV].includes(fileType) && (
                  <ExcelViewer path={fileObj.path} name={fileObj.name} fileType={fileType} />
                )}
              </div>
              <div>
                {fileType === FILE_TYPE.JSON && (
                  <JsonViewer path={fileObj.path} name={fileObj.name} />
                )}
              </div>
              <div>
                {fileType === FILE_TYPE.DOCX && (
                  <DocViewer path={fileObj.path} name={fileObj.name} />
                )}
              </div>
            </>
          ) : (
            <pre data-testid="error-file" style={{ whiteSpace: 'pre-wrap', fontSize: 16 }}>
              {t('DASHBOARD_BUSINESS_DATA_UPLOADS_NOT_FILE_FOUND')}
            </pre>
          )}
        </div>
      )}
    </>
  );
};

export default ViewFilePage;
